import { FC, useMemo, useState } from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import Carousel from 'react-multi-carousel'
import { useRouter } from 'next/router'

import { buildSpaceDetailHref } from 'features/space/lib/buildSpaceDetailHref'
import { RecommendedSpace } from 'types/space/recommended'
import styles from './RecommendedSpaceCard.module.scss'
import { SpaceBooking } from 'state/ducks/locations/models'

// configs
import { PRICE_SIZE_TYPE_BAG, PRICE_SIZE_TYPE_SUITCASE } from 'configs/space'

// types
import { RecommendedSpacePhoto } from 'types/space/recommended'

// components
import PlanCard from 'components/cards/PlanCard'
import RatingCustomComponent from 'components/ratings/RatingCustomComponent'

// hooks
import { useCardScrollTracking } from 'hooks/analytics/useCardScrollTracking'

interface Props {
  space: RecommendedSpace
  position: number
}

type BookingWithCapacity = SpaceBooking & { capacity?: number | null; is_holiday?: boolean }

const RecommendedSpaceCard: FC<Props> = ({ space, position }): JSX.Element => {
  const { t } = useTranslation()

  const [isOpenSizePopUp, setIsOpenSizePopUp] = useState<boolean>(false)

  const { locale } = useRouter()
  const spaceDetailHref = useMemo(
    () =>
      buildSpaceDetailHref({
        locale,
        spaceType: 'normal',
        spaceUuid: space.id,
        encryptedId: space.encrypted_id,
      }) ?? '#',
    [space.id, space.encrypted_id, locale]
  )

  // 個別カードのスクロール追跡
  const {
    ref: cardRef,
    isVisible,
    hasTracked,
  } = useCardScrollTracking({
    position,
    eventName: 'recommended_space_card_scroll',
    category: 'engagement',
    label: 'recommended_space_card',
    threshold: 0.5,
  })

  const carouselResponsive = {
    desktop: {
      breakpoint: { max: 3000, min: 992 },
      items: 1,
    },
    tablet: {
      breakpoint: { max: 991, min: 576 },
      items: 1,
    },
    mobile: {
      breakpoint: { max: 575, min: 0 },
      items: 1,
    },
  }

  const availabilityIcons = {
    circle: {
      src: '/empty_status_circle_03.svg',
      alt: '◯',
    },
    solid: {
      src: '/empty_status_solid_03.svg',
      alt: '-',
    },
  }

  const getOptimizationBooking = (booking_data: BookingWithCapacity) => {
    const rawCapacity = booking_data.capacity
    const parsedCapacity =
      typeof rawCapacity === 'number'
        ? rawCapacity
        : rawCapacity !== undefined && rawCapacity !== null
        ? Number(rawCapacity)
        : null
    const normalizedCapacity =
      parsedCapacity !== null && !Number.isNaN(parsedCapacity) ? Math.floor(parsedCapacity) : null

    const rawRatio = booking_data.ratio
    const ratio =
      typeof rawRatio === 'number'
        ? rawRatio
        : rawRatio !== undefined && rawRatio !== null
        ? Number(rawRatio)
        : 0

    const isHoliday = booking_data.isHoliday ?? booking_data.is_holiday ?? false

    let iconImage: typeof availabilityIcons.circle | null = availabilityIcons.circle
    let remainingLabel = ''

    if (normalizedCapacity !== null) {
      if (normalizedCapacity <= 0) {
        iconImage = availabilityIcons.solid
      } else if (normalizedCapacity <= 2) {
        iconImage = null
        remainingLabel = t('AREAS.CITY.SLUG.REMAINING_AVAILABLE_SLOTS', {
          count: normalizedCapacity,
        })
      } else {
        iconImage = availabilityIcons.circle
      }
    } else {
      if (ratio !== 0) {
        iconImage = availabilityIcons.circle
      }
    }

    if (ratio >= 100 || isHoliday) {
      iconImage = availabilityIcons.solid
      remainingLabel = ''
    }

    // 日付のフォーマット
    const _date = new Date(booking_data.date)
    const month: number = _date.getMonth() + 1
    const day: number = _date.getDate()
    const date: string = `${month}/${day}`

    return {
      date,
      iconImage,
      remainingLabel,
    }
  }

  return (
    <div className={styles.spaceCard} ref={cardRef}>
      {/* デバッグ用の表示（開発環境のみ） */}
      {process.env.NODE_ENV === 'development' && (
        <div
          style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            background: hasTracked ? 'rgba(0,255,0,0.8)' : 'rgba(255,0,0,0.8)',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '10px',
            zIndex: 10,
          }}
        >
          {hasTracked ? '表示済み' : '未表示'} (位置: {position})
        </div>
      )}

      {space.space_photos && space.space_photos.length > 0 ? (
        <div className={styles.spaceCardMainImage}>
          <Carousel
            ssr
            responsive={carouselResponsive}
            infinite
            swipeable={false}
            arrows={true}
            className={styles.spaceCardMainImageList}
            customLeftArrow={
              <button
                className={`${styles.spaceCardMainImageListArrow} ${styles.spaceCardMainImageListArrowLeft}`}
              />
            }
            customRightArrow={
              <button
                className={`${styles.spaceCardMainImageListArrow} ${styles.spaceCardMainImageListArrowRight}`}
              />
            }
          >
            {space.space_photos.map((photo: RecommendedSpacePhoto, index) => {
              return (
                <div key={index} className={styles.spaceCardMainImageListItem}>
                  <img
                    className={styles.spaceCardMainImageImage}
                    src={photo.url}
                    alt={photo.photo_description ? photo.photo_description : ''}
                  />
                </div>
              )
            })}
          </Carousel>
        </div>
      ) : (
        <div className={styles.spaceCardMainImage}>
          <div className={styles.spaceCardMainImageListItem}>
            <img
              className={styles.spaceCardMainImageImage}
              src="/location_popular_area_default.webp"
              alt=""
            />
          </div>
        </div>
      )}
      <div className={styles.spaceCardContent}>
        <Link href={spaceDetailHref}>
          <a className={styles.spaceCardContentLink}>
            <h3 className={styles.spaceCardTitle}>{space.name}</h3>
            <div className={styles.spaceCardDistanceTime}>
              <span className={styles.spaceCardDistanceTimeText}>
                <img
                  className={styles.spaceCardDistanceTimeImage}
                  src="/map_pin_icon_gray.svg"
                  alt=""
                />
                {t('COMMON.HOW_MANY_MINUTES_WALK_FROM_THE_STATION', {
                  stationName: space.station_1_name,
                  minutes: space.distance_from_station_1,
                })}
              </span>
              <span className={styles.spaceCardDistanceTimeText}>
                <img
                  className={styles.spaceCardDistanceTimeImage}
                  src="/business_hours_icon_gray.svg"
                  alt=""
                />
                {t('COMMON.TODAY_BUSINESS_TIME')} :{' '}
                {space.available_24_hour
                  ? t('SPACES.LABEL.OPEN_24_HOURS')
                  : space.open_at_today || space.close_at_today
                  ? `${space.open_at_today}〜${space.close_at_today}`
                  : t('SPACES.LABEL.CLOSED')}
              </span>
            </div>
            {!!space.reviews && !!space.reviews.total_review_count && (
              <div className={styles.spaceCardReview}>
                <div className={styles.spaceCardReviewData}>
                  <span className={styles.spaceCardReviewDataAverage}>
                    {(space.reviews.average_review || 0).toFixed(1)}
                  </span>
                  <span className={styles.spaceCardReviewDataStar}>
                    <RatingCustomComponent
                      averageRating={space.reviews.average_review}
                      maxStar={5}
                      starSize={12}
                    />
                  </span>
                  <span className={styles.spaceCardReviewDataTotal}>
                    {t('COMMON.TOTAL_REVIEWS', {
                      total: space.reviews.total_review_count,
                    })}
                  </span>
                </div>
                {!!space.reviews.comment?.trim() && (
                  <div className={styles.spaceCardReviewComment}>
                    <p className={styles.spaceCardReviewCommentText}>{space.reviews.comment}</p>
                  </div>
                )}
              </div>
            )}
          </a>
        </Link>
      </div>
      <div className={styles.spaceCardSize}>
        <dl className={styles.spaceCardSizeContent}>
          <dt className={styles.spaceCardSizeTitle}>
            <Link href={spaceDetailHref}>
              <a className={styles.spaceCardSizeTitleLink}>{t('COMMON.CAN_STORE_LUGGAGE_COUNT')}</a>
            </Link>
            <span
              className={styles.spaceCardSizeTitleIcon}
              onClick={() => {
                setIsOpenSizePopUp(!isOpenSizePopUp)
              }}
            >
              <img
                className={styles.spaceCardSizeTitleImage}
                src="/question_icon_gray.svg"
                alt=""
              />
              <div
                className={`${styles.spaceCardSizeSizePopUp} ${
                  isOpenSizePopUp ? styles.spaceCardSizeSizePopUpOpen : ''
                }`}
              >
                <div className={styles.spaceCardSizeSizePopUpClose}>
                  <button
                    className={styles.spaceCardSizeSizePopUpCloseButton}
                    onClick={() => {
                      setIsOpenSizePopUp(false)
                    }}
                  >
                    <img src="/close_icon_black.svg" alt="" />
                  </button>
                </div>
                <PlanCard
                  title={t('AREAS.CITY.SLUG.PLAN_BAG_SIZE')}
                  countryCode={'jpn'}
                  iconImage={'/plan_size_bag.webp'}
                  sizeType={PRICE_SIZE_TYPE_BAG}
                  description={t('AREAS.CITY.SLUG.PLAN_BAG_DESCRIPTION')}
                  price={space.prices.bag_size}
                />
                <PlanCard
                  title={t('AREAS.CITY.SLUG.PLAN_SUIT_SIZE')}
                  countryCode={'jpn'}
                  iconImage={'/plan_size_suitcase.webp'}
                  sizeType={PRICE_SIZE_TYPE_SUITCASE}
                  description={t('AREAS.CITY.SLUG.PLAN_SUIT_DESCRIPTION')}
                  price={space.prices.suitecase_size}
                />
              </div>
            </span>
          </dt>
          <dd className={styles.spaceCardSizeDescription}>
            <Link href={spaceDetailHref}>
              <a className={styles.spaceCardSizeDescriptionLink}>
                <span className={styles.spaceCardSizeInfo}>
                  <img className={styles.spaceCardSizeInfoImage} src="/luggage_icon.svg" alt="" />
                  {t('COMMON.SUITECASE_SIZE')} :{' '}
                  <strong className={styles.spaceCardSizeInfoCount}>{space.suitcase_size}</strong>
                </span>
                <span className={styles.spaceCardSizeInfo}>
                  <img className={styles.spaceCardSizeInfoImage} src="/bag_icon_black.svg" alt="" />
                  {t('COMMON.BAG_SIZE')} :{' '}
                  <strong className={styles.spaceCardSizeInfoCount}>{space.bag_size}</strong>
                </span>
              </a>
            </Link>
          </dd>
        </dl>
      </div>
      <div className={styles.spaceCardEmpty}>
        <Link href={spaceDetailHref}>
          <a className={styles.spaceCardEmptyLink}>
            <div className={styles.spaceCardEmptyCalendarTable}>
              <div
                className={`${styles.spaceCardEmptyCalendarTableItem} ${styles.spaceCardEmptyCalendarTableLabel}`}
              >
                <span className={styles.spaceCardEmptyCalendarTableLabelText}>
                  {t('COMMON.AVAILABILITY_TIME')}
                </span>
              </div>
              {space.bookings?.map((booking, index) => {
                const bookingData = getOptimizationBooking(booking as BookingWithCapacity)
                return (
                  <div key={index} className={styles.spaceCardEmptyCalendarTableItem}>
                    <div
                      key={index}
                      className={`${styles.spaceCardEmptyCalendarTableTh} ${
                        booking.day_of_week_label === 'Sat'
                          ? styles.spaceCardEmptyCalendarTableThSaturday
                          : ''
                      } ${
                        booking.day_of_week_label === 'Sun'
                          ? styles.spaceCardEmptyCalendarTableThSunday
                          : ''
                      }`}
                    >
                      <span className={styles.spaceCardEmptyCalendarTableDate}>
                        <span>{bookingData.date}</span>
                        <span>{booking.day_of_week}</span>
                      </span>
                    </div>
                    <div className={styles.spaceCardEmptyCalendarTableTd}>
                      <div className={styles.spaceCardEmptyCalendarTableTdIcon}>
                        {bookingData.iconImage ? (
                          <img src={bookingData.iconImage.src} alt={bookingData.iconImage.alt} />
                        ) : (
                          <span className={styles.spaceCardEmptyCalendarTableTdLabel}>
                            {bookingData.remainingLabel || '\u00A0'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </a>
        </Link>
      </div>
      <p className={styles.spaceCardReserve}>
        <Link href={spaceDetailHref}>
          <a className={styles.spaceCardReserveButtonLink}>
            <span className={styles.spaceCardReserveButton}>{t('COMMON.RESERVE_THIS_SPACE')}</span>
          </a>
        </Link>
      </p>
    </div>
  )
}

export default RecommendedSpaceCard
